/**
 * TypeScript view of the `star_battle` format.
 * Source of truth: `shared/schema/star_battle.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

export interface StarBattleDefinition {
  puzzleType: "star_battle";
  typeVersion: number;
  /** The grid is `size` × `size`, split into `size` regions. */
  size: number;
  /** Stars in every row, column and region. */
  stars: number;
  /** One string per row, one region id (a letter or a digit) per cell. */
  regions: string[];
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const STAR = "*";
export const EMPTY = ".";
/** A solving aid: the player is sure the cell holds no star. Never sent to the server. */
export const DOT = "x";
export type Mark = typeof STAR | typeof DOT | typeof EMPTY;

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface StarBattleProgress {
  version: 1;
  /** One string per row, `*` a star, `x` dotted, `.` undecided. */
  rows: string[];
}

/** One string per row, `*` a star and `.` anything else: dots are folded into `.`. */
export interface StarBattleSubmission {
  rows: string[];
}

/** The author's stars, in the submission format. */
export type StarBattleSolution = StarBattleSubmission;

export const MAX_SIZE = 20;
export const MAX_STARS = 3;
/** Region ids, in the order the editor hands them out. */
export const REGION_IDS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export type Cell = [row: number, col: number];

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
